import type { RxDatabase } from "rxdb";
import { RXDB_EPOCUS_BILLING_COLLECTION_NAME } from "../collections/epocus-billing";
import { RXDB_EPOCUS_BILLING_STATUS_COLLECTION_NAME } from "../collections/epocus-billing-status";
import { RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME } from "../collections/epocus-consent-document";
import { RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME } from "../collections/epocus-encounter";
import { RXDB_EPOCUS_EXAM_COLLECTION_NAME } from "../collections/epocus-exam";
import { RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME } from "../collections/epocus-patient-consent";
import { RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME } from "../collections/epocus-probe-association";
import { RXDB_EPOCUS_PROGRAM_COLLECTION_NAME } from "../collections/epocus-program";
import { RXDB_EPOCUS_REVIEW_COLLECTION_NAME } from "../collections/epocus-review";
import { RXDB_IMAGING_INSTANCE_COLLECTION_NAME } from "../collections/imaging-instance";
import { RXDB_IMAGING_STUDY_COLLECTION_NAME } from "../collections/imaging-study";
import type { ReplicationState, RxDBCollections } from "../provider";
import { useReplicateEpocusBilling } from "../replication/epocus-billing";
import { useReplicateEpocusBillingStatus } from "../replication/epocus-billing-status";
import { useReplicateEpocusConsentDocument } from "../replication/epocus-consent-document";
import { useReplicateEpocusEncounter } from "../replication/epocus-encounter";
import { useReplicateEpocusExam } from "../replication/epocus-exam";
import { useReplicateEpocusPatientConsent } from "../replication/epocus-patient-consent";
import { useReplicateEpocusProbeAssociation } from "../replication/epocus-probe-association";
import { useReplicateEpocusProgram } from "../replication/epocus-program";
import { useReplicateEpocusReview } from "../replication/epocus-review";
import { useReplicateImagingInstance } from "../replication/imaging-instance";
import { useReplicateImagingStudy } from "../replication/imaging-study";

export const useRxdbReplication = (
  db: RxDatabase<RxDBCollections>,
): ReplicationState => {
  const imagingStudy = useReplicateImagingStudy({
    db,
    collection: db?.[RXDB_IMAGING_STUDY_COLLECTION_NAME],
  });

  const imagingInstance = useReplicateImagingInstance({
    db,
    collection: db?.[RXDB_IMAGING_INSTANCE_COLLECTION_NAME],
  });

  const epocusBilling = useReplicateEpocusBilling({
    db,
    collection: db?.[RXDB_EPOCUS_BILLING_COLLECTION_NAME],
  });

  const epocusBillingStatus = useReplicateEpocusBillingStatus({
    db,
    collection: db?.[RXDB_EPOCUS_BILLING_STATUS_COLLECTION_NAME],
  });

  const epocusEncounter = useReplicateEpocusEncounter({
    db,
    collection: db?.[RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME],
  });

  const epocusExam = useReplicateEpocusExam({
    db,
    collection: db?.[RXDB_EPOCUS_EXAM_COLLECTION_NAME],
  });

  const epocusPatientConsent = useReplicateEpocusPatientConsent({
    db,
    collection: db?.[RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME],
  });

  const epocusConsentDocument = useReplicateEpocusConsentDocument({
    db,
    collection: db?.[RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME],
  });

  const epocusProbeAssociation = useReplicateEpocusProbeAssociation({
    db,
    collection: db?.[RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME],
  });

  const epocusProgram = useReplicateEpocusProgram({
    db,
    collection: db?.[RXDB_EPOCUS_PROGRAM_COLLECTION_NAME],
  });

  const epocusReview = useReplicateEpocusReview({
    db,
    collection: db?.[RXDB_EPOCUS_REVIEW_COLLECTION_NAME],
  });

  const hasUnsyncedChanges = !!(
    imagingStudy.earliestNonPushedChangeAt ||
    imagingInstance.earliestNonPushedChangeAt ||
    epocusBilling.earliestNonPushedChangeAt ||
    epocusEncounter.earliestNonPushedChangeAt ||
    epocusExam.earliestNonPushedChangeAt ||
    epocusProbeAssociation.earliestNonPushedChangeAt ||
    epocusReview.earliestNonPushedChangeAt
  );

  return {
    imagingStudy,
    imagingInstance,
    "epocus-billing": epocusBilling,
    "epocus-billing-status": epocusBillingStatus,
    epocusEncounter,
    epocusExam,
    epocusPatientConsent,
    epocusConsentDocument,
    epocusProbeAssociation,
    "epocus-program": epocusProgram,
    epocusReview,
    "epocus-referral": { isPulling: false },
    "epocus-referral-outcome": { isPulling: false },
    hasUnsyncedChanges,
  };
};
